/**
 * §18.7 — every employee's year to date: the opening totals and the year's
 * posted runs, the figures the year-end forms will be drawn from (payroll
 * stage 4).
 *
 * THE SUMS ARE THE SERVER'S. GET /payroll/year-to-date adds the year up, stops
 * each wage at its base (apps/api/src/payroll/year-to-date.ts) and adds the
 * employees together, so this screen is not a second place that counts the
 * year.
 *
 * WHAT IS NOT COUNTED IS SAID. A run whose entry is still a draft is named with
 * a link to it, as the TWC worksheet names one; the note says voided and
 * reversed runs are left out; and how each employee's column was made up is
 * written under the table.
 *
 * A COLUMN PER EMPLOYEE, and a Total only when there is more than one: with one
 * it would repeat the column beside it, which is why the register leaves out
 * its By Employee table for one person.
 *
 * A ROW NOBODY HAS IS LEFT OUT, as the run page leaves out a bonus nobody was
 * paid: bonuses, health insurance premiums and Additional Medicare. Every other
 * row shows even at $0.00, since a year that withheld nothing is a figure the
 * forms need.
 *
 * The year is kept in the address, as the worksheets keep their quarter, and
 * offered from the years the server names, so the business's own year can
 * always be chosen again.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type YearToDateFigures, type YearToDateReport } from '../api.js';
import { payCounted } from '../lib/labels.js';
import { formatCents } from '../lib/money.js';

interface Row {
  key: keyof YearToDateFigures;
  label: string;
  /** Left out when nobody has any. */
  onlyWhenAny?: boolean;
  /** Part of the row above, shown under it as the run page shows a bonus under the gross. */
  part?: boolean;
  strong?: boolean;
}

const ROWS: Row[] = [
  { key: 'grossPayCents', label: 'Gross Pay' },
  { key: 'supplementalWagesCents', label: 'Of which bonuses', part: true, onlyWhenAny: true },
  { key: 'healthPremiumCents', label: 'Health Insurance Premiums', onlyWhenAny: true },
  { key: 'incomeTaxWagesCents', label: 'Federal Income Tax Wages' },
  { key: 'incomeTaxWithheldCents', label: 'Federal Income Tax Withheld' },
  { key: 'socialSecurityWagesCents', label: 'Social Security Wages' },
  { key: 'socialSecurityTaxCents', label: 'Social Security Withheld' },
  { key: 'medicareWagesCents', label: 'Medicare Wages' },
  { key: 'medicareTaxCents', label: 'Medicare Withheld' },
  { key: 'additionalMedicareTaxCents', label: 'Additional Medicare Withheld', onlyWhenAny: true },
  { key: 'futaTaxableWagesCents', label: 'FUTA Taxable Wages' },
  { key: 'texasUnemploymentTaxableWagesCents', label: 'Texas Unemployment Taxable Wages' },
  { key: 'netPayCents', label: 'Net Pay', strong: true },
];

export function YearToDatePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [report, setReport] = useState<YearToDateReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getYearToDate(year)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not read the year to date.');
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  // The year in the address, even while it loads or after it failed, so the
  // choice on screen is never one year while the page reads another.
  const shown = year ?? report?.year;
  const choices = report === null ? [] : [...new Set([...report.years, ...(shown === undefined ? [] : [shown])])].sort((a, b) => b - a);

  return (
    <section className="year-to-date">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        Year to Date{' '}
        <InfoNote label="What Is Counted">
          Each employee&apos;s year: pay entered by hand before the first pay run, recorded as opening totals, and every
          pay run whose entry is posted. A run still a draft is named above the table and not counted, and voided and
          reversed runs are left out. Social Security, FUTA and Texas unemployment wages stop at each year&apos;s wage
          base; Medicare wages have none. Federal income tax wages include a 2% shareholder&apos;s health insurance
          premiums, which are never paid in cash, so they are in neither the gross pay nor the net pay. The year-end
          forms will be drawn from these figures.
        </InfoNote>
      </h1>

      {report !== null && shown !== undefined && (
        <div className="row report-period">
          <label>
            Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (report === null || report.year !== shown) && <p className="muted">Loading…</p>}
      {error === null && report !== null && report.year === shown && <Year report={report} />}
    </section>
  );
}

function Year({ report }: { report: YearToDateReport }) {
  const several = report.employees.length > 1;
  const rows = ROWS.filter((row) => !row.onlyWhenAny || report.totals[row.key] > 0);
  const drafts = report.draftRuns.length;

  return (
    <>
      {drafts > 0 && (
        <div className="notice notice-warn">
          {drafts === 1 ? `One run in ${report.year} is` : `${drafts} runs in ${report.year} are`} still a draft and not
          counted:
          <ul className="missed-list">
            {report.draftRuns.map((run) => (
              <li key={run.runId}>
                <Link to={`/payroll/runs/${run.runId}`}>
                  {run.employeeName}, paid {run.payDate}
                </Link>
              </li>
            ))}
          </ul>
          Post {drafts === 1 ? 'it' : 'them'} to count {drafts === 1 ? 'it' : 'them'} here.
        </div>
      )}

      {report.employees.length === 0 ? (
        <p className="muted">Nothing is counted for {report.year}.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="lines" aria-label="Year to Date">
              <thead>
                <tr>
                  <th>{report.year}</th>
                  {report.employees.map((employee) => (
                    <th key={employee.employeeId} className="num">
                      {employee.employeeName}
                    </th>
                  ))}
                  {several && <th className="num">Total</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const shownAs = (cents: number) => (row.strong ? <strong>{formatCents(cents)}</strong> : formatCents(cents));
                  const tone = row.part ? ' muted' : '';
                  return (
                    <tr key={row.key}>
                      <td className={row.part ? 'muted' : undefined}>{row.strong ? <strong>{row.label}</strong> : row.label}</td>
                      {report.employees.map((employee) => (
                        <td key={employee.employeeId} className={`num${tone}`}>
                          {shownAs(employee.figures[row.key])}
                        </td>
                      ))}
                      {several && <td className={`num${tone}`}>{shownAs(report.totals[row.key])}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {report.employees.map((employee) => (
            <p key={employee.employeeId} className="muted">
              {employee.employeeName}: {payCounted(employee)}.
            </p>
          ))}
        </>
      )}
    </>
  );
}
